import {
  MapPin,
  Path,
  Phone,
  X,
  Globe,
  ArrowLeft,
} from '@phosphor-icons/react';
import {
  VendorBusinessLocationWithDeals,
  VendorType,
  VendorTypeDisplayNames,
} from '../../../../../../types/apiTypes';
import { Deal } from '../../../../../../types/dbTypes';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { sanitizeUrl } from '../../../../../../utils/SanitizationUtils';
import HoursOfOperationWidget from '../../../../../signup/vendors/widgets/HoursOfOperationsWidget';
import { renderIconBg } from '../VendorMarketplace';

function SelectedLocationDetails({
  distances,
  selectedVendorLocation,
  setSelectedVendorLocation,
  openDirectionsWindow,
}: {
  selectedVendorLocation: VendorBusinessLocationWithDeals | null;
  distances: { [key: string]: string };
  setSelectedVendorLocation: (
    location: VendorBusinessLocationWithDeals | null
  ) => void;
  openDirectionsWindow: (
    selectedVendorLocation: VendorBusinessLocationWithDeals
  ) => void;
}) {
  const { screenView } = useScreenView();

  const renderImageUrlPath = (
    selectedVendorLocation: VendorBusinessLocationWithDeals | null
  ) => {
    if (
      selectedVendorLocation?.vendor?.business_name === 'Penske Truck Rental'
    ) {
      return '/penske-logo.png';
    } else if (
      selectedVendorLocation?.vendor?.business_name === 'Ryder Truck Rental'
    ) {
      return '/logo-ryder.svg';
    } else if (
      selectedVendorLocation?.vendor?.business_name === 'Bridgestone Tires'
    ) {
      return '/bridgestone_logo.svg';
    }
    return selectedVendorLocation?.imageUrl;
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="absolute right-96 top-20 z-10 h-[600px] min-h-[600px] w-[345px] overflow-x-hidden overflow-y-scroll rounded-[4px] bg-white p-3">
            <div className="relative flex h-full w-full flex-col gap-y-[24px]">
              {renderImageUrlPath(selectedVendorLocation) && (
                <img
                  alt="vendor location"
                  src={renderImageUrlPath(selectedVendorLocation)}
                  className="h-auto max-h-[210px] w-auto max-w-[321px] rounded-[4px]"
                />
              )}
              <div
                onClick={() => setSelectedVendorLocation(null)}
                className="absolute right-2 top-2 cursor-pointer rounded-full bg-white p-2 shadow-2xl drop-shadow-lg"
              >
                <X size={24} className="text-black" />
              </div>
              <div className="flex flex-col space-y-1">
                <div className="flex flex-col justify-between">
                  <h2 className="line-clamp-1 w-[90%] overflow-ellipsis py-1 text-[18px] font-[600] text-primary">
                    <div className="flex flex-col">
                      <h2 className="max-w-[200px] text-wrap text-[18px] font-[600] text-primary">
                        {selectedVendorLocation?.vendor?.business_name}
                      </h2>
                      {selectedVendorLocation?.location_name !==
                        selectedVendorLocation?.vendor?.business_name && (
                        <h2 className="max-w-[200px] text-wrap text-[14px] font-[600] text-graytertiary">
                          {selectedVendorLocation?.location_name}
                        </h2>
                      )}
                    </div>
                  </h2>
                  <div className="flex flex-row items-center space-x-2 text-[12px] text-gray-500">
                    <div className="flex flex-row items-center space-x-[2px] rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[4px] py-[3px] align-middle">
                      <MapPin size={16} />
                      <span>
                        {distances[selectedVendorLocation?.location_id ?? '']}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="h-[20px] max-h-[20px] w-[321px] max-w-[321px] overflow-ellipsis">
                  <p className="line-clamp-1 text-[14px] text-sm text-gray-500">
                    {selectedVendorLocation?.description_of_services}
                  </p>
                </div>
                <p className="text-[14px] font-[600] text-primary">
                  Primary Service
                </p>
                <div className="z-20 grid grid-cols-3 items-center gap-1 align-middle">
                  <div
                    style={{
                      backgroundColor: renderIconBg(
                        selectedVendorLocation?.primary_vendor_type ?? ''
                      ),
                    }}
                    className="max-w-full whitespace-normal break-words rounded-[8px] px-2 py-0.5 text-center text-[12px] font-semibold leading-tight text-white"
                  >
                    {
                      VendorTypeDisplayNames[
                        selectedVendorLocation?.primary_vendor_type as VendorType
                      ]
                    }
                  </div>
                </div>
                {selectedVendorLocation?.secondary_vendor_types &&
                  selectedVendorLocation?.secondary_vendor_types?.length >
                    0 && (
                    <p className="text-[14px] font-[600] text-primary">
                      Other Services Offered
                    </p>
                  )}
                <div className="z-20 grid grid-cols-3 items-center gap-1 align-middle">
                  {selectedVendorLocation?.secondary_vendor_types
                    ?.split(',')
                    .map((type) => {
                      return (
                        <div
                          key={type}
                          style={{ backgroundColor: renderIconBg(type) }}
                          className="max-w-full whitespace-normal break-words rounded-[8px] px-2 py-0.5 text-center text-[12px] font-semibold leading-tight text-white"
                        >
                          {VendorTypeDisplayNames[type as VendorType]}
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className="flex w-full flex-row items-center justify-between space-x-2 align-middle">
                {selectedVendorLocation && (
                  <button
                    onClick={() => openDirectionsWindow(selectedVendorLocation)}
                    className="flex w-[50%] flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#1D213B] border-opacity-[8%] bg-primarybg px-3 py-[6px] align-middle"
                  >
                    <Path size={24} className="text-primary" />
                    <p className="text-[14px] text-primary">Route</p>
                  </button>
                )}
                {/* <button className="flex w-[50%] flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#1D213B] border-opacity-[8%] bg-primarybg px-3 py-[6px] align-middle">
                  <ShareNetwork size={24} className="text-primary" />
                  <p className="text-[14px] text-primary">Share</p>
                </button> */}
              </div>
              <div className="flex w-full flex-row items-center space-x-2 align-middle font-[600]">
                <MapPin size={24} className="text-primary opacity-50" />
                <p className="text-[14px] text-primary">
                  {selectedVendorLocation?.address_st_number +
                    ' ' +
                    selectedVendorLocation?.address_st_name +
                    (selectedVendorLocation?.address_unit
                      ? ' ' + selectedVendorLocation?.address_unit
                      : '') +
                    ', ' +
                    selectedVendorLocation?.address_city +
                    ', ' +
                    selectedVendorLocation?.address_state +
                    ' ' +
                    selectedVendorLocation?.address_zip}
                </p>
              </div>
              {selectedVendorLocation?.hours_of_operation && (
                <div className="flex flex-col gap-y-1">
                  <p className="text-[14px] text-primary">WORKING HOURS</p>
                  {selectedVendorLocation?.hours_of_operation && (
                    <div className="flex flex-col text-[14px] text-primary opacity-90">
                      <HoursOfOperationWidget
                        hoursOfOperation={JSON.parse(
                          selectedVendorLocation?.hours_of_operation ?? ''
                        )}
                        viewOnly={true}
                      />
                    </div>
                  )}
                </div>
              )}
              {selectedVendorLocation?.deals &&
                selectedVendorLocation?.deals?.length > 0 && (
                  <div className="flex flex-col gap-y-1">
                    <p className="text-[14px] text-primary">PROMOTIONS</p>
                    {selectedVendorLocation?.deals &&
                      selectedVendorLocation?.deals?.length > 0 &&
                      selectedVendorLocation?.deals?.map(
                        (promo: Deal, idxPromo: number) => {
                          return (
                            <div className="flex flex-col" key={idxPromo}>
                              <p className="text-[14px] font-[600] text-primary">
                                {(idxPromo + 1).toString() + '.'} {promo?.title}
                              </p>
                              <p className="whitespace-pre-line text-[14px] text-graysecondary">
                                {promo?.description}
                              </p>
                            </div>
                          );
                        }
                      )}
                  </div>
                )}
              <div className="flex flex-col gap-y-1 pb-3">
                <p className="text-[14px] text-primary">CONTACTS</p>
                <div className="flex flex-col space-y-1">
                  {selectedVendorLocation?.website_url && (
                    <div className="flex flex-row items-center space-x-1 align-middle">
                      <Globe size={16} className="text-primary opacity-50" />
                      <a
                        className="text-[14px] text-primary underline"
                        href={sanitizeUrl(selectedVendorLocation?.website_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedVendorLocation?.website_url}
                      </a>
                    </div>
                  )}
                  <div className="flex flex-row items-center space-x-1 align-middle">
                    <Phone size={16} className="text-primary opacity-50" />
                    <a
                      href={`tel:${selectedVendorLocation?.business_phone_number}`}
                      className="text-[14px] text-primary underline"
                    >
                      {selectedVendorLocation?.business_phone_number}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div className="absolute z-40 ml-[21.25%] h-[700px] min-h-[700px] w-[57.5%] overflow-x-hidden overflow-y-scroll rounded-[4px] bg-white p-3 drop-shadow-xl">
            <div className="relative flex h-full w-full flex-col gap-y-[24px]">
              {renderImageUrlPath(selectedVendorLocation) && (
                <img
                  alt="vendor location"
                  src={renderImageUrlPath(selectedVendorLocation)}
                  className="h-auto max-h-[210px] w-auto max-w-[321px] rounded-[4px]"
                />
              )}
              <div
                onClick={() => setSelectedVendorLocation(null)}
                className="absolute right-2 top-2 cursor-pointer rounded-full bg-white p-2 shadow-2xl drop-shadow-lg"
              >
                <X size={24} className="text-black" />
              </div>
              <div className="flex flex-col space-y-1">
                <div className="flex flex-col justify-between">
                  <h2 className="line-clamp-1 w-[62.5%] overflow-ellipsis py-1 text-[18px] font-[600] text-primary">
                    <div className="flex flex-col">
                      <h2 className="max-w-[200px] text-wrap text-[18px] font-[600] text-primary">
                        {selectedVendorLocation?.vendor?.business_name}
                      </h2>
                      {selectedVendorLocation?.location_name !==
                        selectedVendorLocation?.vendor?.business_name && (
                        <h2 className="max-w-[200px] text-wrap text-[14px] font-[600] text-graytertiary">
                          {selectedVendorLocation?.location_name}
                        </h2>
                      )}
                    </div>
                  </h2>
                  <div className="flex flex-row items-center space-x-2 text-[12px] text-gray-500">
                    <div className="flex flex-row items-center space-x-[2px] rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[4px] py-[3px] align-middle">
                      <MapPin size={16} />
                      <span>
                        {distances[selectedVendorLocation?.location_id ?? '']}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="h-[20px] max-h-[20px] w-[321px] max-w-[321px] overflow-ellipsis">
                  <p className="line-clamp-1 text-[14px] text-sm text-gray-500">
                    {selectedVendorLocation?.description_of_services}
                  </p>
                </div>
                <p className="text-[14px] font-[600] text-primary">
                  Primary Service
                </p>
                <div className="z-20 grid grid-cols-3 items-center gap-1 align-middle">
                  <div
                    style={{
                      backgroundColor: renderIconBg(
                        selectedVendorLocation?.primary_vendor_type ?? ''
                      ),
                    }}
                    className={`rounded-[8px] px-2 text-center text-[12px] font-semibold text-white`}
                  >
                    {
                      VendorTypeDisplayNames[
                        selectedVendorLocation?.primary_vendor_type as VendorType
                      ]
                    }
                  </div>
                </div>
                {selectedVendorLocation?.secondary_vendor_types &&
                  selectedVendorLocation?.secondary_vendor_types?.length >
                    0 && (
                    <p className="text-[14px] font-[600] text-primary">
                      Other Services Offered
                    </p>
                  )}
                <div className="z-20 grid grid-cols-3 items-center gap-1 align-middle">
                  {selectedVendorLocation?.secondary_vendor_types
                    ?.split(',')
                    .map((type) => {
                      return (
                        <div
                          style={{ backgroundColor: renderIconBg(type) }}
                          key={type}
                          className={`rounded-[8px] px-2 text-center text-[12px] font-semibold text-white`}
                        >
                          {VendorTypeDisplayNames[type as VendorType]}
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className="flex w-full flex-row items-center justify-between space-x-2 align-middle">
                {selectedVendorLocation && (
                  <button
                    onClick={() => openDirectionsWindow(selectedVendorLocation)}
                    className="flex w-[50%] flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#1D213B] border-opacity-[8%] bg-primarybg px-3 py-[6px] align-middle"
                  >
                    <Path size={24} className="text-primary" />
                    <p className="text-[14px] text-primary">Route</p>
                  </button>
                )}
                {/* <button className="flex w-[50%] flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#1D213B] border-opacity-[8%] bg-primarybg px-3 py-[6px] align-middle">
                  <ShareNetwork size={24} className="text-primary" />
                  <p className="text-[14px] text-primary">Share</p>
                </button> */}
              </div>
              <div className="flex w-full flex-row items-center space-x-2 align-middle font-[600]">
                <MapPin size={24} className="text-primary opacity-50" />
                <p className="text-[14px] text-primary">
                  {selectedVendorLocation?.address_st_number +
                    ' ' +
                    selectedVendorLocation?.address_st_name +
                    (selectedVendorLocation?.address_unit
                      ? ' ' + selectedVendorLocation?.address_unit
                      : '') +
                    ' ' +
                    selectedVendorLocation?.address_city +
                    ' ' +
                    selectedVendorLocation?.address_state +
                    ' ' +
                    selectedVendorLocation?.address_zip}
                </p>
              </div>
              {selectedVendorLocation?.hours_of_operation && (
                <div className="flex flex-col gap-y-1">
                  <p className="text-[14px] text-primary">WORKING HOURS</p>
                  {selectedVendorLocation?.hours_of_operation && (
                    <div className="flex flex-col text-[14px] text-primary opacity-90">
                      <HoursOfOperationWidget
                        hoursOfOperation={JSON.parse(
                          selectedVendorLocation?.hours_of_operation ?? ''
                        )}
                        viewOnly={true}
                      />
                    </div>
                  )}
                </div>
              )}
              {selectedVendorLocation?.deals &&
                selectedVendorLocation?.deals?.length > 0 && (
                  <div className="flex flex-col gap-y-1">
                    <p className="text-[14px] text-primary">PROMOTIONS</p>
                    {selectedVendorLocation?.deals &&
                      selectedVendorLocation?.deals?.length > 0 &&
                      selectedVendorLocation?.deals?.map(
                        (promo: Deal, idxPromo: number) => {
                          return (
                            <div className="flex flex-col" key={idxPromo}>
                              <p className="text-[14px] font-[600] text-primary">
                                {(idxPromo + 1).toString() + '.'} {promo?.title}
                              </p>
                              <p className="whitespace-pre-line text-[14px] text-graysecondary">
                                {promo?.description}
                              </p>
                            </div>
                          );
                        }
                      )}
                  </div>
                )}
              <div className="flex flex-col gap-y-1 pb-3">
                <p className="text-[14px] text-primary">CONTACTS</p>
                <div className="flex flex-col space-y-1">
                  {selectedVendorLocation?.website_url && (
                    <div className="flex flex-row items-center space-x-1 align-middle">
                      <Globe size={16} className="text-primary opacity-50" />
                      <a
                        className="text-[14px] text-primary underline"
                        href={sanitizeUrl(selectedVendorLocation?.website_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedVendorLocation?.website_url}
                      </a>
                    </div>
                  )}
                  <div className="flex flex-row items-center space-x-1 align-middle">
                    <Phone size={16} className="text-primary opacity-50" />
                    <a
                      href={`tel:${selectedVendorLocation?.business_phone_number}`}
                      className="text-[14px] text-primary underline"
                    >
                      {selectedVendorLocation?.business_phone_number}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div className="absolute -top-14 z-40 h-[100vh] w-[100vw] overflow-x-hidden overflow-y-scroll bg-white p-3 drop-shadow-xl">
            <div className="relative flex h-full w-full flex-col gap-y-[24px]">
              {renderImageUrlPath(selectedVendorLocation) && (
                <img
                  alt="vendor location"
                  src={renderImageUrlPath(selectedVendorLocation)}
                  className="h-auto max-h-[210px] w-auto max-w-[321px] rounded-[4px]"
                />
              )}
              <div
                onClick={() => setSelectedVendorLocation(null)}
                className="absolute right-2 top-2 cursor-pointer rounded-full bg-white p-2 shadow-2xl drop-shadow-lg"
              >
                <ArrowLeft size={24} className="text-black" />
              </div>
              {/* <div
                onClick={() => setSelectedVendorLocation(null)}
                className="absolute right-2 top-2 cursor-pointer rounded-full bg-white p-2 shadow-2xl drop-shadow-lg"
              >
                <X size={24} className="text-black" />
              </div> */}
              <div className="flex flex-col space-y-1">
                <div className="flex flex-col justify-between">
                  <h2 className="line-clamp-2 w-[57.5%] overflow-ellipsis py-1 font-[600] text-primary">
                    <div className="flex flex-col">
                      <h2 className="max-w-[200px] text-wrap text-[18px] font-[600] text-primary">
                        {selectedVendorLocation?.vendor?.business_name}
                      </h2>
                      {selectedVendorLocation?.location_name !==
                        selectedVendorLocation?.vendor?.business_name && (
                        <h2 className="max-w-[200px] text-wrap text-[14px] font-[600] text-graytertiary">
                          {selectedVendorLocation?.location_name}
                        </h2>
                      )}
                    </div>
                  </h2>
                  <div className="flex flex-row items-center space-x-1 text-[12px] text-gray-500">
                    <div className="flex flex-row items-center space-x-[2px] rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[4px] py-[3px] align-middle">
                      <MapPin size={16} />
                      <span>
                        {distances[selectedVendorLocation?.location_id ?? '']}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="h-[20px] max-h-[20px] w-[321px] max-w-[321px] overflow-ellipsis">
                  <p className="line-clamp-1 text-[14px] text-sm text-gray-500">
                    {selectedVendorLocation?.description_of_services}
                  </p>
                </div>
                <p className="text-[14px] font-[600] text-primary">
                  Primary Service
                </p>
                <div className="z-20 grid grid-cols-3 items-center gap-1 align-middle">
                  <div
                    style={{
                      backgroundColor: renderIconBg(
                        selectedVendorLocation?.primary_vendor_type ?? ''
                      ),
                    }}
                    className={`rounded-[8px] px-2 text-center text-[12px] font-semibold text-white`}
                  >
                    {
                      VendorTypeDisplayNames[
                        selectedVendorLocation?.primary_vendor_type as VendorType
                      ]
                    }
                  </div>
                </div>
                {selectedVendorLocation?.secondary_vendor_types &&
                  selectedVendorLocation?.secondary_vendor_types?.length >
                    0 && (
                    <p className="text-[14px] font-[600] text-primary">
                      Other Services Offered
                    </p>
                  )}
                <div className="z-20 grid grid-cols-3 items-center gap-1 align-middle">
                  {selectedVendorLocation?.secondary_vendor_types
                    ?.split(',')
                    .map((type) => {
                      return (
                        <div
                          style={{ backgroundColor: renderIconBg(type) }}
                          key={type}
                          className={`rounded-[8px] px-2 text-center text-[12px] font-semibold text-white`}
                        >
                          {VendorTypeDisplayNames[type as VendorType]}
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className="flex w-full flex-row items-center justify-between space-x-2 align-middle">
                {selectedVendorLocation && (
                  <button
                    onClick={() => openDirectionsWindow(selectedVendorLocation)}
                    className="flex w-[50%] flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#1D213B] border-opacity-[8%] bg-primarybg px-3 py-[6px] align-middle"
                  >
                    <Path size={24} className="text-primary" />
                    <p className="text-[14px] text-primary">Route</p>
                  </button>
                )}
                {/* <button className="flex w-[50%] flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#1D213B] border-opacity-[8%] bg-primarybg px-3 py-[6px] align-middle">
                  <ShareNetwork size={24} className="text-primary" />
                  <p className="text-[14px] text-primary">Share</p>
                </button> */}
              </div>
              <div className="flex w-full flex-row items-center space-x-2 align-middle font-[600]">
                <MapPin size={24} className="text-primary opacity-50" />
                <p className="text-[14px] text-primary">
                  {selectedVendorLocation?.address_st_number +
                    ' ' +
                    selectedVendorLocation?.address_st_name +
                    (selectedVendorLocation?.address_unit
                      ? ' ' + selectedVendorLocation?.address_unit
                      : '') +
                    ' ' +
                    selectedVendorLocation?.address_city +
                    ' ' +
                    selectedVendorLocation?.address_state +
                    ' ' +
                    selectedVendorLocation?.address_zip}
                </p>
              </div>
              {selectedVendorLocation?.hours_of_operation && (
                <div className="flex flex-col gap-y-1">
                  <p className="text-[14px] text-primary">WORKING HOURS</p>
                  {selectedVendorLocation?.hours_of_operation && (
                    <div className="flex flex-col text-[14px] text-primary opacity-90">
                      <HoursOfOperationWidget
                        hoursOfOperation={JSON.parse(
                          selectedVendorLocation?.hours_of_operation ?? ''
                        )}
                        viewOnly={true}
                      />
                    </div>
                  )}
                </div>
              )}
              {selectedVendorLocation?.deals &&
                selectedVendorLocation?.deals?.length > 0 && (
                  <div className="flex flex-col gap-y-1">
                    <p className="text-[14px] text-primary">PROMOTIONS</p>
                    {selectedVendorLocation?.deals &&
                      selectedVendorLocation?.deals?.length > 0 &&
                      selectedVendorLocation?.deals?.map(
                        (promo: Deal, idxPromo: number) => {
                          return (
                            <div className="flex flex-col" key={idxPromo}>
                              <p className="text-[14px] font-[600] text-primary">
                                {(idxPromo + 1).toString() + '.'} {promo?.title}
                              </p>
                              <p className="whitespace-pre-line text-[14px] text-graysecondary">
                                {promo?.description}
                              </p>
                            </div>
                          );
                        }
                      )}
                  </div>
                )}
              <div className="flex flex-col gap-y-1 pb-3">
                <p className="text-[14px] text-primary">CONTACTS</p>
                <div className="flex flex-col space-y-1">
                  {selectedVendorLocation?.website_url && (
                    <div className="flex flex-row items-center space-x-1 align-middle">
                      <Globe size={16} className="text-primary opacity-50" />
                      <a
                        className="text-[14px] text-primary underline"
                        href={sanitizeUrl(selectedVendorLocation?.website_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedVendorLocation?.website_url}
                      </a>
                    </div>
                  )}
                  <div className="flex flex-row items-center space-x-1 align-middle">
                    <Phone size={16} className="text-primary opacity-50" />
                    <a
                      href={`tel:${selectedVendorLocation?.business_phone_number}`}
                      className="text-[14px] text-primary underline"
                    >
                      {selectedVendorLocation?.business_phone_number}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default SelectedLocationDetails;
